import {
  CONTRACT_COMPLIANCE,
  CONTRACT_COMPLIANCE_HEADER_DESCP,
  CONTRACT_REVIEW_PENDING_HEADER_DESCP,
  CONTRACTS_COMPLETE_REVIEW,
  CONTRACTS_COMPLETE_REVIEW_HEADER_DESCP,
  CONTRACTS_PENDING_CLAUSE_CHECK,
  CONTRACTS_PENDING_CLAUSE_CHECK_HEADER_DESCP,
  CONTRACTS_REVIEW_PENDING,
  DASHBOARD_FEATURES,
  getMimeTypeAsPerFileExtension,
  GREEN,
  headerActionsForClauseCheck,
  headerActionsForContractReview,
  hideMetaDataForClauseCheck,
  hideMetaDataForContractReview,
} from "./constants";
import {
  ClauseCheckReviewType,
  ContractsReviewType,
  HeaderActionsType,
  ReviewSummaryType,
  SummaryMetricsType,
} from "./types";
import { addDocExtentionIfMissing, getFileNameWithoutExtension } from "./utils";

export class MetaData {
  vertical: string;
  contractId: string;
  simbaId: string;
  documentId: string;
  vendor_name: string;
  service_type: string;
  contract_type: string;
  maximum_total_cost: string;
  start_date: any;
  end_date: any;
  maximum_billable_hours: any;
  constructor(obj: any) {
    this.vertical = obj?.vertical || "";
    this.contractId = obj?.contract_id || "";
    this.simbaId = obj?.simba_id || "";
    this.documentId = obj?.document_id || "";
    this.vendor_name = obj?.vendor_name || "";
    this.service_type = obj?.service_type || "";
    this.contract_type = obj?.contract_type || "";
    this.maximum_total_cost = `$${obj?.maximum_total_cost || 0}`;
    this.start_date = obj?.start_date || "";
    this.end_date = obj?.end_date || "";
    this.maximum_billable_hours = obj?.maximum_billable_hours || 0;
  }
}

export class ContractDocument {
  file_name: string;
  display_name!: string;
  created_at!: string;
  status!: string;
  metadata: MetaData;
  id: string;
  driveDetails: {};
  file_id?: string;
  loading?: boolean;
  error?: {};

  constructor(obj: any) {
    this.file_name = obj?.file_name || obj?.name;
    this.created_at = obj?.created_at;
    this.status = obj?.status;
    this.metadata = new MetaData(
      obj?.doc_meta?.metadata || obj?.doc_meta || obj?.clause_meta
    );
    this.id = obj?.id;
    this.display_name =
      obj?.display_name ||
      `${this.metadata?.vendor_name} ${this.metadata?.contract_type}`.trim() ||
      getFileNameWithoutExtension(obj?.name) ||
      this.file_name;
    this.driveDetails = obj?.drive_details;
    this.file_id = obj?.drive_details?.file_id || obj?.id;
  }
}

export class ContractReviewModel {
  headerText: string;
  headerActions: Array<HeaderActionsType>;
  user_docs: SummaryMetricsType[];
  pending_documents: ContractsReviewType;
  review_summary: Array<ReviewSummaryType>;
  recommendedPercentage: string;
  hideMetaData: string[];
  constructor(obj: any) {
    this.headerText = DASHBOARD_FEATURES.CONTRACT_REDLINING;
    this.headerActions = headerActionsForContractReview;
    this.pending_documents = {
      list:
        obj?.pending_documents?.map((doc: any) => new ContractDocument(doc)) ||
        [],
      fileUpload: {
        isUploading: false,
        error: {},
        uploaded: false,
      },
    };
    this.review_summary =
      [{ category: "Recommendations" }].concat(obj?.review_summary || []) || [];
    this.recommendedPercentage = (
      (obj?.user_docs?.completed_docs / obj?.user_docs?.total_docs) *
      100
    ).toFixed(2);
    this.user_docs = [
      {
        count: obj?.user_docs?.pending_docs || 0,
        text: CONTRACTS_REVIEW_PENDING,
        descp: CONTRACT_REVIEW_PENDING_HEADER_DESCP,
      },
      {
        count: obj?.user_docs?.completed_docs || 0,
        text: CONTRACTS_COMPLETE_REVIEW,
        color: GREEN,
        descp: CONTRACTS_COMPLETE_REVIEW_HEADER_DESCP,
      },
    ];
    this.hideMetaData = hideMetaDataForContractReview;
  }
}

export class ClauseCheckModel {
  headerText: string;
  headerActions: Array<HeaderActionsType>;
  user_docs: SummaryMetricsType[];
  pending_documents: ClauseCheckReviewType;
  review_summary: Array<ReviewSummaryType>;
  recommendedPercentage: number;
  hideGraph: boolean;
  hideMetaData: string[];
  uploadProcessCompleted?: boolean;
  failed_documents: any;

  constructor(obj: any) {
    this.headerText = DASHBOARD_FEATURES.CLAUSE_CHECK;
    this.headerActions = headerActionsForClauseCheck;
    this.pending_documents = {
      list:
        obj?.pending_documents?.list?.map(
          (doc: any) => new ContractDocument(doc)
        ) || [],
    };
    this.review_summary =
      [{ category: "Standard Clauses" }].concat(
        obj?.review_summary || obj?.category_counts || []
      ) || [];

    this.recommendedPercentage =
      (obj?.user_docs?.completed_docs / obj?.user_docs?.total_docs) * 100;
    this.user_docs = [
      {
        count: obj?.pending_count || 0,
        text: CONTRACTS_PENDING_CLAUSE_CHECK,
        descp: CONTRACTS_PENDING_CLAUSE_CHECK_HEADER_DESCP,
      },
      {
        count: obj?.completed_count || 0,
        text: CONTRACT_COMPLIANCE,
        color: GREEN,
        descp: CONTRACT_COMPLIANCE_HEADER_DESCP,
      },
    ];
    this.hideGraph = true;
    this.hideMetaData = hideMetaDataForClauseCheck;
    this.uploadProcessCompleted = true;
  }
}

export class Suggestion {
  text: string;
  constructor(obj: any) {
    this.text = obj;
  }
}

export class ClauseCheckDocumentRequestModel {
  file_name: string;
  file_id: string;
  constructor(obj: any) {
    this.file_name = addDocExtentionIfMissing(obj);
    this.file_id = obj?.id || new Date().getTime() + obj?.idx;
  }
}

export class TTCDocumentRequestModel {
  file_name: string;
  file_id: string;
  constructor(obj: any) {
    this.file_name = addDocExtentionIfMissing(obj);
    this.file_id = obj?.id;
  }
}

export class TTCDocumentRequestModalForLocalUpload {
  id: string;
  file_id: string;
  name: string;
  mimeType: string;
  gcs_file_name: string;
  constructor(obj: any) {
    this.file_id = obj?.id;
    this.id = obj?.id;
    this.name = addDocExtentionIfMissing(obj);
    this.mimeType =
      obj?.mimeType ||
      obj.type ||
      getMimeTypeAsPerFileExtension(obj?.file_name || obj?.name);
    this.gcs_file_name = obj?.gcs_file_name;
  }
}

export class ProcessedFileForTTC {
  file_name: string;
  mimeType: string;
  id: string;
  file_name_with_extension: string;
  file_id: string;
  gcs_file_name: string;
  constructor(obj: any) {
    this.file_name = addDocExtentionIfMissing({
      name: obj?.name || obj?.file_name,
    });
    this.mimeType = getMimeTypeAsPerFileExtension(this.file_name);
    this.id = obj?.id;
    this.file_name_with_extension = addDocExtentionIfMissing({
      name: obj?.name || obj?.file_name,
    });
    this.file_id = obj?.file_id;
    this.gcs_file_name = obj?.gcs_file_name || "";
  }
}

// export class RequestBodyForClauseCheckAdvanced {
//   vertical_id: string;
//   files: string[];
//   drive_folder_ids: string[];
//   drive_file_ids: string[];
//   token: string;

//   constructor(obj: any) {
//     this.vertical_id = obj?.vertical_id || "";
//     this.files = obj?.files || [];
//     this.drive_folder_ids =
//       obj?.driveFiles
//         ?.filter((file: any) => file?.type === "folder")
//         ?.map((file: any) => file.id) || [];
//     this.drive_file_ids =
//       obj?.driveFiles
//         ?.filter((file: any) => file?.type === "file")
//         ?.map((file: any) => file.id) || [];
//     this.token = obj?.token || "";
//   }
// }

export class RequestBodyForClauseCheckAdvanced {
  vertical_id: string;
  files?: File[];
  drive_folder_ids?: string[];
  drive_file_ids?: string[];
  token: string;

  constructor(obj: any) {
    this.vertical_id = obj?.vertical_id || "";
    this.token = obj?.token || "";
    
    // Only include properties that have actual values
    if (obj?.files && obj.files.length > 0) {
      this.files = obj.files;
    }
    
    const driveFolders = obj?.driveFiles
      ?.filter((file: any) => file?.type === "folder")
      ?.map((file: any) => file.id) || [];
    if (driveFolders.length > 0) {
      this.drive_folder_ids = driveFolders;
    }
    
    const driveFiles = obj?.driveFiles
      ?.filter((file: any) => file?.type === "file")
      ?.map((file: any) => file.id) || [];
    if (driveFiles.length > 0) {
      this.drive_file_ids = driveFiles;
    }
  }

  isValid(): boolean {
    return !!(this.files?.length || this.drive_folder_ids?.length || this.drive_file_ids?.length);
  }
}
